import { useCallback } from 'react'

import { paradigmConnectDesk, paradigmLinkAccount, paradigmListAccounts } from '@/service/venue.api'
import type {
  ParadigmConnectDeskResponse,
  ParadigmLinkAccountResponse,
  ParadigmListAccountsResponse,
} from '@/types/generated/public_api'

/**
 * All three RPCs reject with a bare proto `Error` (`{ message }`), not an `Error`
 * instance — `String(e)` on one of those renders "[object Object]" and discards the
 * backend text ("Paradigm is not available on your plan", "Admin access to this account
 * is required"), which is exactly the text these surfaces exist to show verbatim.
 */
export function paradigmErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error) return error.message
  if (typeof (error as { message?: unknown })?.message === 'string') {
    return (error as { message: string }).message
  }
  return fallback
}

/**
 * The three-step Paradigm desk binding wizard: register desk credentials, list the
 * desk's account nicknames, bind one to this OrderX account. Deribit or Bybit only, and
 * Admin-gated server-side — the callers surface those refusals verbatim rather than
 * pre-gating on their own guess at the rules.
 *
 * There is deliberately no unlink: the backend exposes none, and `linkAccount` is an
 * unconditional overwrite, so re-linking a different nickname is the whole of "manage".
 */
export function useParadigmBinding() {
  // Credentials are passed straight through and never retained here — the sheet clears
  // its own fields once the desk is registered.
  const connectDesk = useCallback(
    (
      accountId: string,
      accessKey: string,
      secretKey: string
    ): Promise<ParadigmConnectDeskResponse> =>
      paradigmConnectDesk({ accountId, accessKey, secretKey }),
    []
  )

  const listAccounts = useCallback(
    (accountId: string): Promise<ParadigmListAccountsResponse> =>
      paradigmListAccounts({ accountId }),
    []
  )

  const linkAccount = useCallback(
    (accountId: string, accountName: string): Promise<ParadigmLinkAccountResponse> =>
      paradigmLinkAccount({ accountId, accountName }),
    []
  )

  return { connectDesk, listAccounts, linkAccount }
}

// Disconnect is a mutation, not a plain promise like the three wizard steps: it has to
// invalidate the accounts query for the settings row to fall back to "not connected".
// It therefore lives with the Derive/Hyperliquid disconnects it is shaped after, and is
// re-exported here so every Paradigm surface still has one import to reach for.
export { useParadigmDisconnect } from '@/service/venue.hook'
